import { CARD_TYPE, isValidPesel } from 'utils/validation'

// Klucze bloku `customer` z koszyka (= inputy POST /cart/customer) -> klucze w formSlice
export const USER_FIELDS = {
  first_name: 'firstName',
  surname: 'lastName',
  street: 'street',
  building_nr: 'houseNumber',
  apartment_nr: 'localNumber',
  zip_code: 'postalCode',
  city: 'city',
  pesel: 'pesel',
  identity_card_type: 'cardType',
  identity_card: 'identityCard',
}

// Klucze bloku `customerCompany` (inputy formularza firmy) -> klucze w formSlice
export const COMPANY_FIELDS = {
  name: 'companyName',
  nip: 'companyNip',
  streetName: 'companyStreet',
  homeNr: 'companyHouseNumber',
  flatNr: 'companyLocalNumber',
  postalCode: 'companyPostalCode',
  cityName: 'companyCity',
}

export const USER_NAME_FIELDS = ['first_name', 'surname']
export const USER_ADDRESS_FIELDS = ['street', 'building_nr', 'apartment_nr', 'zip_code', 'city']
export const USER_IDENTITY_FIELDS = ['pesel', 'identity_card_type', 'identity_card']
export const COMPANY_ADDRESS_FIELDS = ['streetName', 'homeNr', 'flatNr', 'postalCode', 'cityName']

export const OPTIONAL_FIELDS = ['apartment_nr', 'flatNr']

// Pola firmy poprawiane przy uzupełnianiu danych do umowy na ekranie faktury — NIP identyfikuje firmę, więc go nie zmieniamy
export const COMPANY_EDIT_FIELDS = Object.keys(COMPANY_FIELDS).filter(f => f !== 'nip')

export const IDENTITY = {
  none: 'none',
  pesel: 'pesel',
  document: 'document',
  peselOrDocument: 'pesel_or_document',
  peselAndDocument: 'pesel_and_document',
}

export const DEFAULT_DOCUMENT_TYPES = [CARD_TYPE.idCard, CARD_TYPE.drivingLicense, CARD_TYPE.passport]

/** Czy walidator koszyka zgłasza problem z polem (brak / zła wartość) */
export const hasIssue = (block, field) => !!block && (
  !!block.missing?.includes(field) ||
  !!block.invalid?.includes(field) ||
  (!!block.fields?.[field] && block.fields[field].status !== 'ok')
)

/** Pierwszy komunikat walidatora dla pola: { code, message } */
export const getServerError = (block, field) => {
  const error = Array.isArray(block?.errors) ? block.errors.find(e => e.field === field) : undefined
  if (error) return error
  const code = block?.fields?.[field]?.code
  return code ? { code, message: '' } : undefined
}

/** Wartości z bloku koszyka -> wartości formularza */
export const toFormValues = (data, map) => Object
  .entries(map)
  .reduce((result, [apiKey, formKey]) => {
    const value = data?.[apiKey]
    result[formKey] = value === null || value === undefined ? '' : String(value)
    return result
  }, {})

export const isPolishCountry = (country) => !country || ['polska', 'poland', 'pl'].includes(String(country).trim().toLowerCase())

/**
 * Które grupy pól pokazać w formularzu uzupełniania.
 * Pokazujemy tylko grupy, w których walidator koszyka zgłasza problem — reszta danych jest już poprawna.
 */
export const getVisibleSections = (customerValidation) => {
  const user = customerValidation?.user
  const company = customerValidation?.company
  const identity = customerValidation?.requirements?.identity || IDENTITY.none

  const showUser = user?.valid === false
  const showCompany = company?.valid === false

  const userNames = showUser ? USER_NAME_FIELDS.filter(f => hasIssue(user, f)) : []
  let userAddress = showUser && USER_ADDRESS_FIELDS.some(f => hasIssue(user, f))
  let userIdentity = showUser && USER_IDENTITY_FIELDS.some(f => hasIssue(user, f))
  // walidator nie wskazał konkretnych pól — pokaż komplet danych do umowy
  if (showUser && userNames.length === 0 && !userAddress && !userIdentity) {
    userAddress = true
    userIdentity = identity !== IDENTITY.none
  }

  const companyAddress = showCompany && COMPANY_ADDRESS_FIELDS.some(f => hasIssue(company, f))
  let companyFields = showCompany ? ['name', 'nip'].filter(f => hasIssue(company, f)) : []
  if (companyAddress) companyFields = [...companyFields, ...COMPANY_ADDRESS_FIELDS]
  if (showCompany && companyFields.length === 0) companyFields = Object.keys(COMPANY_FIELDS)

  return {
    identity,
    documentTypes: customerValidation?.requirements?.identityDocumentTypes?.length
      ? customerValidation.requirements.identityDocumentTypes
      : DEFAULT_DOCUMENT_TYPES,
    userNames,
    userAddress,
    userIdentity,
    companyFields,
  }
}

const isFilled = value => value !== null && value !== undefined && String(value).trim() !== ''

/**
 * Lokalne sprawdzenie danych względem `customerValidation.requiredInputs.<typ>` z koszyka.
 * Sprawdza tylko obecność pól (requiredFields + grupy oneOfRequiredFields) oraz to, co da się
 * sprawdzić bez backendu (suma kontrolna PESEL). Ostateczna walidacja zostaje po stronie koszyka.
 * @returns {string[]} nazwy brakujących / błędnych inputów; pusta tablica = komplet
 */
export const getMissingInputs = (data, spec) => {
  if (!spec) return []
  const missing = (spec.requiredFields || []).filter(f => !isFilled(data?.[f]))

  for (const { alternatives = [] } of spec.oneOfRequiredFields || []) {
    const satisfied = alternatives.some(group => group.every(f => isFilled(data?.[f])))
    if (!satisfied) alternatives.flat().forEach(f => !missing.includes(f) && missing.push(f))
  }

  if (isFilled(data?.pesel) && !isValidPesel(String(data.pesel).replace(/[\s-]/g, '')) && !missing.includes('pesel')) {
    missing.push('pesel')
  }

  return missing
}

/** Sekcje formularza Contract/Form do pełnej edycji danych klienta (wg requiredInputs) */
export const getCustomerSections = (spec, requirements) => {
  const identity = spec?.identity || requirements?.identity || IDENTITY.none
  return {
    identity,
    documentTypes: requirements?.identityDocumentTypes?.length
      ? requirements.identityDocumentTypes
      : DEFAULT_DOCUMENT_TYPES,
    userNames: USER_NAME_FIELDS,
    userAddress: true,
    userIdentity: identity !== IDENTITY.none,
    companyFields: [],
  }
}
